import { range } from "lodash"
import { Audio } from "../../../utils/Audio"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { ActivityBackground } from "../sprites/ActivitiesBackground"
import { TtAtlas, TtAudio } from "../TobaccoTourAssets"
import { ChewTourState } from "./ChewTourState"
import { SmokelessTourAnimations } from "./SmokelessTourAnimations"
import { SmokelessTourStages } from "./SmokelessTourTypes"

export class SmokelessTourSnuffAnimation {
    private static INTRO = "INTRO"
    private static OPEN_CAN = "OPEN_CAN"
    private static SNUFF_BUBBLE = "SNUFF_BUBBLE"
    private static SHRINK_CAN = "SHRINK_CAN"
    private static SNUFF_MOUTH = "SNUFF_MOUTH"
    private static MOUTH_BUBBLE = "MOUTH_BUBBLE"
    private static SNUFF_HEAD = "SNUFF_HEAD"

    sprite: Phaser.Sprite

    static play = (
        game: Phaser.Game,
        background: ActivityBackground,
        state: ChewTourState,
    ) => {
        const animation = new SmokelessTourSnuffAnimation(game)
        Audio.add(game, TtAudio.SNUFF_NARRATOR, {
            onPlay: () => animation.introAnimation(),
            onStop: () => {
                animation.sprite.destroy(true)
                background.close()
                state.snuff.grayOut()
                state.completed = SmokelessTourStages.SNUFF
            },
        })
    }

    // TODO: lots of repeated patterns in this class, likely a way to make a generic way to handle animation timing?
    private constructor(private game: Phaser.Game) {
        this.sprite = Sprites.add(game, TtAtlas.SNUFF_ANIMATION, {
            x: Scale.x(0.215),
            y: Scale.y(0.208),
        })

        this.sprite.animations.add(
            SmokelessTourSnuffAnimation.INTRO,
            range(15),
            12,
        )
        this.sprite.animations.add(
            SmokelessTourSnuffAnimation.OPEN_CAN,
            range(15, 25),
            12,
        )
        this.sprite.animations.add(
            SmokelessTourSnuffAnimation.SNUFF_BUBBLE,
            range(25, 35),
            12,
        )
        this.sprite.animations.add(
            SmokelessTourSnuffAnimation.SHRINK_CAN,
            range(35, 50),
            12,
        )
        this.sprite.animations.add(
            SmokelessTourSnuffAnimation.SNUFF_MOUTH,
            range(65, 75),
            12,
        )
        this.sprite.animations.add(SmokelessTourSnuffAnimation.MOUTH_BUBBLE, [
            75,
        ])
        this.sprite.animations.add(
            SmokelessTourSnuffAnimation.SNUFF_HEAD,
            range(76, 85),
            12,
        )
    }

    private introAnimation = () => {
        const current = this.sprite.animations.play(
            SmokelessTourSnuffAnimation.INTRO,
        )
        current.onComplete.addOnce(() => {
            const timer = this.game.time.create(true)
            timer.add(2000, this.openCanAnimation)
            timer.start()
        })
    }

    private openCanAnimation = () => {
        const current = this.sprite.animations.play(
            SmokelessTourSnuffAnimation.OPEN_CAN,
        )
        current.onComplete.addOnce(this.snuffBubbleAnimation)
    }

    private snuffBubbleAnimation = () => {
        const current = this.sprite.animations.play(
            SmokelessTourSnuffAnimation.SNUFF_BUBBLE,
        )
        current.onComplete.addOnce(() => {
            const timer = this.game.time.create()
            timer.add(2000, this.shrinkCanAnimation)
            timer.start()
        })
    }

    private shrinkCanAnimation = () => {
        const current = this.sprite.animations.play(
            SmokelessTourSnuffAnimation.SHRINK_CAN,
        )
        current.onComplete.addOnce(this.handDipAnimation)
    }

    private handDipAnimation = () => {
        const handSprite = Sprites.add(this.game, TtAtlas.HAND_DIP, {
            x: Scale.x(0.47),
            y: Scale.y(0.287),
        })
        handSprite.animations.add("HAND_FULL", [1])

        const downTween = this.game.add
            .tween(handSprite)
            .to({ y: Scale.y(0.46) }, 1000, Phaser.Easing.Linear.None)
        downTween.onComplete.addOnce(() =>
            handSprite.animations.play("HAND_FULL"),
        )
        const upTween = this.game.add
            .tween(handSprite)
            .to({ y: Scale.y(0.287) }, 1000, Phaser.Easing.Linear.None)

        const timer = this.game.time.create(true)
        timer.add(1000, () => {
            handSprite.destroy()
            this.snuffMouthAnimation()
        })

        upTween.onComplete.addOnce(() => timer.start())

        downTween.chain(upTween)
        downTween.start()
    }

    private snuffMouthAnimation = () => {
        const current = this.sprite.animations.play(
            SmokelessTourSnuffAnimation.SNUFF_MOUTH,
        )
        const timer = this.game.time.create(true)
        timer.add(1000, this.mouthBubbleAnimation)
        current.onComplete.addOnce(() => timer.start())
    }

    private mouthBubbleAnimation = () => {
        const current = this.sprite.animations.play(
            SmokelessTourSnuffAnimation.MOUTH_BUBBLE,
        )
        const timer = this.game.time.create(true)
        let saliva: Phaser.Sprite
        timer.add(6000, () => (saliva = this.salivaAnimation()))
        timer.add(9000, () => {
            saliva.destroy()
            this.snuffHeadAnimation()
        })
        current.onComplete.addOnce(() => timer.start())
    }

    private salivaAnimation = () => {
        const saliva = SmokelessTourAnimations.saliva(
            this.game,
            Scale.x(0.402),
            Scale.y(0.46),
        )
        saliva.animations.play(SmokelessTourAnimations.SALIVA)
        return saliva
    }

    private snuffHeadAnimation = () => {
        const current = this.sprite.animations.play(
            SmokelessTourSnuffAnimation.SNUFF_HEAD,
        )
        current.onComplete.addOnce(() => {
            const spitting = SmokelessTourAnimations.spitting(
                this.game,
                Scale.x(0.236),
                Scale.y(0.247),
            )
            const spitAnimation = spitting.animations.play(
                SmokelessTourAnimations.SPITTING,
            )
            spitAnimation.onComplete.addOnce(() => {
                spitting.destroy()
                const swallowing = SmokelessTourAnimations.swallowing(
                    this.game,
                    Scale.x(0.416),
                    Scale.y(0.587),
                )
                const swallowAnimation = swallowing.animations.play(
                    SmokelessTourAnimations.SWALLOWING,
                )
                swallowAnimation.onComplete.addOnce(() => swallowing.destroy())
            })
        })
    }
}
